import { Button } from '@1trading/react/components/ui/button';
import { Loading } from '@1trading/react/components/ui/loading';
import { useToast } from '@1trading/react/hooks/use-toast';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { GuardedFormStepper, useGuardedFormStepper } from '@/components/ui/guarded-form-stepper';
import LoadingCard from '@/components/ui/loading-card';
import { PageCard, PageCardContent, PageCardFooter } from '@/components/ui/page-card';
import { PageHeading, PageHeadingBackButton, PageHeadingDescription, PageHeadingTitle } from '@/components/ui/page-heading';
import { InstitutionOnboardingProgressStep, useProgress } from '@/context/progress';
import { InstitutionGoToReviewStepperButton } from '@/features/institution/components/go-to-review-button';
import { useInstitutionOnboardingBasePath } from '@/features/institution/context/update';
import { useDetailsFlowControl } from '@/features/institution/hooks/useDetailsFlowControl';
import { useInstitutionDetailsMutation, useInstitutionDetailsV2Answers, useInstitutionDetailsVersion } from '@/features/institution/hooks/useInstitutionDetails';
import {
  INSTITUTION_KYC_CDD_DEFAULTS,
  type InstitutionKycCdd,
  institutionDetailsKycCddApiToInstitutionKycCdd,
  institutionKycCddCategoriesSchema,
  institutionKycCddDueDiligenceSchema,
  institutionKycCddToInstitutionDetailsKycCddApi,
} from '@/features/institution/kyc-cdd/schema';
import { getDetailsKeyProgress, INSTITUTION_DETAILS_TYPE } from '@/features/institution/utils';
import type { FormStep } from '@/types/components';
import { InstitutionKycCddFieldsetCategories, InstitutionKycCddFieldsetCategoriesLegend } from './fieldsets/categories';
import { InstitutionKycCddFieldsetCustomerDueDiligence } from './fieldsets/customer-due-diligence';
import { InstitutionKycCddFieldsetOwnershipAndRisk } from './fieldsets/ownership-and-risk';
import { InstitutionKycCddFieldsetPolicy } from './fieldsets/policy';
import { InstitutionKycCddFieldsetScreening } from './fieldsets/screening';

const InstitutionKycCddFormStepDueDiligenceLegend = () => {
  const { t } = useTranslation();

  return (
    <PageHeading asChild>
      <legend>
        <PageHeadingTitle>{t('institutionDetails.kycCdd.title')}</PageHeadingTitle>
        <PageHeadingDescription>{t('institutionDetails.kycCdd.description')}</PageHeadingDescription>
      </legend>
    </PageHeading>
  );
};
InstitutionKycCddFormStepDueDiligenceLegend.displayName = 'InstitutionKycCddFormStepDueDiligenceLegend';

const InstitutionKycCddFormStepDueDiligence = () => (
  <div className="space-y-6">
    <InstitutionKycCddFieldsetPolicy />
    <InstitutionKycCddFieldsetCustomerDueDiligence />
    <InstitutionKycCddFieldsetOwnershipAndRisk />
    <InstitutionKycCddFieldsetScreening />
  </div>
);
InstitutionKycCddFormStepDueDiligence.displayName = 'InstitutionKycCddFormStepDueDiligence';

const FORM_STEPS = {
  DUE_DILIGENCE: {
    Component: InstitutionKycCddFormStepDueDiligence,
    Legend: InstitutionKycCddFormStepDueDiligenceLegend,
    schema: institutionKycCddDueDiligenceSchema,
  },
  CATEGORIES: {
    Component: InstitutionKycCddFieldsetCategories,
    Legend: InstitutionKycCddFieldsetCategoriesLegend,
    schema: institutionKycCddCategoriesSchema,
  },
} satisfies Record<string, FormStep<InstitutionKycCdd>>;

const STEPS = Object.values(FORM_STEPS).map((step) => step.Component);
const STEP_LEGENDS = Object.values(FORM_STEPS).map((step) => step.Legend);
const STEP_SCHEMAS = Object.values(FORM_STEPS).map((step) => step.schema);

const InstitutionKycCddFormCurrentStep = () => {
  const { currentStep, totalSteps } = useGuardedFormStepper<InstitutionKycCdd>();
  const { data: version } = useInstitutionDetailsVersion();
  const { setCurrentStep } = useProgress();
  const Step = STEPS[currentStep];

  useEffect(() => {
    if (!version) return;

    return setCurrentStep(
      InstitutionOnboardingProgressStep.INSTITUTION_INFORMATION,
      getDetailsKeyProgress(INSTITUTION_DETAILS_TYPE.ONBOARDING, version, 'kycCdd', currentStep, totalSteps),
    );
  }, [currentStep, setCurrentStep, totalSteps, version]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: We want to scroll when currentStep changes
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [currentStep]);

  if (!Step) throw new Error('Step not found');

  return (
    <PageCardContent className="pt-8">
      <div className="animate-in fade-in" key={currentStep}>
        <Step />
      </div>
    </PageCardContent>
  );
};
InstitutionKycCddFormCurrentStep.displayName = 'InstitutionKycCddFormCurrentStep';

const InstitutionKycCddFormCurrentStepLegend = () => {
  const { currentStep } = useGuardedFormStepper<InstitutionKycCdd>();
  const Legend = STEP_LEGENDS[currentStep];

  if (!Legend) throw new Error('Legend not found');

  return <Legend />;
};
InstitutionKycCddFormCurrentStepLegend.displayName = 'InstitutionKycCddFormCurrentStepLegend';

const InstitutionKycCddFormBackButton = () => {
  const { currentStep, prevStep } = useGuardedFormStepper<InstitutionKycCdd>();
  const navigate = useNavigate();
  const onboardingPath = useInstitutionOnboardingBasePath();

  const handlePrevStep = useCallback(() => {
    if (currentStep === 0) {
      navigate(`${onboardingPath}/compliance-training`, { replace: true });
    } else {
      prevStep();
    }
  }, [currentStep, prevStep, navigate, onboardingPath]);

  return <PageHeadingBackButton floatingInPage="left" fixedInLayout onClick={handlePrevStep} />;
};
InstitutionKycCddFormBackButton.displayName = 'InstitutionKycCddFormBackButton';

const InstitutionKycCddFormFooter = ({ isPending }: { isPending: boolean }) => {
  const { currentStep, nextStep } = useGuardedFormStepper<InstitutionKycCdd>();
  const { t } = useTranslation();
  const Step = STEPS[currentStep];

  if (Step && 'isAutoNextStep' in Step && Step.isAutoNextStep) return null;

  return (
    <PageCardFooter>
      <Button className="w-full relative" size="lg" variant="primary" disabled={isPending} onClick={() => nextStep()}>
        <span className={isPending ? 'invisible' : ''}>{t('buttons.continue')}</span>
        {isPending ? <Loading variant="inverted" position="overlay" size="sm" /> : null}
      </Button>
    </PageCardFooter>
  );
};
InstitutionKycCddFormFooter.displayName = 'InstitutionKycCddFormFooter';

const InstitutionKycCddForm = () => {
  const { isLoading: isLoadingInstitutionDetailsAnswers, data: institutionDetailsAnswers } = useInstitutionDetailsV2Answers();
  const { isLoading: isLoadingInstitutionDetailsFlow } = useDetailsFlowControl(INSTITUTION_DETAILS_TYPE.ONBOARDING, 'kycCdd');
  const { isPending, mutateAsync } = useInstitutionDetailsMutation();
  const navigate = useNavigate();
  const onboardingPath = useInstitutionOnboardingBasePath();
  const { toast } = useToast();
  const { t } = useTranslation();
  const isLoading = isLoadingInstitutionDetailsAnswers || isLoadingInstitutionDetailsFlow;

  const onSubmit = async (data: InstitutionKycCdd, { isReturningToReview = false }: { isReturningToReview?: boolean } = {}) => {
    await mutateAsync(
      { kycCdd: await institutionKycCddToInstitutionDetailsKycCddApi(data) },
      {
        onError: () => {
          toast({
            description: t('fundamentals.defaultError.message'),
            variant: 'destructive',
          });
        },
        onSuccess: () => {
          navigate(isReturningToReview ? `${onboardingPath}/review` : `${onboardingPath}/expected-activity`, { replace: true });
        },
      },
    );
  };

  if (isLoading) return <LoadingCard />;

  const defaultValues = institutionDetailsAnswers?.kycCdd ? institutionDetailsKycCddApiToInstitutionKycCdd(institutionDetailsAnswers.kycCdd) : INSTITUTION_KYC_CDD_DEFAULTS;

  return (
    <GuardedFormStepper className="relative" defaultValues={defaultValues} steps={STEPS} stepSchemas={STEP_SCHEMAS} onSubmit={onSubmit}>
      <fieldset>
        <InstitutionKycCddFormCurrentStepLegend />
        <div className="flex flex-row justify-between -md:justify-end">
          <InstitutionKycCddFormBackButton />
          <InstitutionGoToReviewStepperButton onSubmit={(data: InstitutionKycCdd) => onSubmit(data, { isReturningToReview: true })} />
        </div>
        <PageCard size="lg" bordered fixedFooter>
          <InstitutionKycCddFormCurrentStep />
          <InstitutionKycCddFormFooter isPending={isPending} />
        </PageCard>
      </fieldset>
    </GuardedFormStepper>
  );
};
InstitutionKycCddForm.displayName = 'InstitutionKycCddForm';

export default InstitutionKycCddForm;
